import { useEffect, useRef } from 'react';
import { CoralogixLogSeverity, CoralogixRum } from '@coralogix/browser';
import { CustomTracer, GlobalSpan } from '@coralogix/browser/src/custom-spans';
import {
    ParentSpan,
    AnalyticsAttributes,
    LogAttributes,
    InstrumentTraceType,
    LogSeverity,
    MetricAttributes,
    TraceType,
} from './attributes';
import { setUserContext, updateLabels } from './observabilityDimensions';

let tracer: CustomTracer | undefined;
let parentSpan: GlobalSpan | undefined;

export class Observability {
    private constructor() {
        // Private constructor to force factory usage
    }

    static createTrace(): {
        trace: Observability;
        closeTrace: () => void;
    } {
        const trace = new Observability();

        if (!tracer) {
            tracer = CoralogixRum.getCustomTracer();
        }

        if (!tracer) {
            /* DO NOT USE .log - only as a fallback */
            CoralogixRum.log(CoralogixLogSeverity.Warn, 'Tracer not initialized');
            return {
                trace,
                closeTrace: () => {},
            };
        }

        if (parentSpan) {
            return {
                trace,
                closeTrace: () => {
                    parentSpan?.endSpan();
                    parentSpan = undefined;
                },
            };
        }

        parentSpan = tracer.startGlobalSpan(ParentSpan, {
            [TraceType]: InstrumentTraceType.ParentSpan,
        });

        if (!parentSpan) {
            /* DO NOT USE .log - only as a fallback */
            CoralogixRum.log(CoralogixLogSeverity.Warn, 'Parent span not initialized');
            return {
                trace,
                closeTrace: () => {},
            };
        }

        return {
            trace,
            closeTrace: () => {
                if (parentSpan) {
                    parentSpan.endSpan();
                    parentSpan = undefined;
                }
            },
        };
    }

    addAnalytics(analyticsAttributes: AnalyticsAttributes, options: { persist: true }): { endAnalytics: () => void };
    addAnalytics(analyticsAttributes: AnalyticsAttributes, options?: { persist?: false }): void;
    addAnalytics(analyticsAttributes: AnalyticsAttributes, options: { persist?: boolean } = { persist: false }) {
        if (!parentSpan) {
            /* DO NOT USE .log - only as a fallback */
            CoralogixRum.log(
                CoralogixLogSeverity.Warn,
                "Couldn't add analytics, parent span has not been created",
                null,
                analyticsAttributes
            );
            if (options.persist) {
                return { endAnalytics: () => {} };
            }
            return;
        }

        const { endSpan } = parentSpan.startCustomSpan(InstrumentTraceType.Analytics, {
            ...analyticsAttributes,
            [TraceType]: InstrumentTraceType.Analytics,
        });

        if (options.persist) {
            return { endAnalytics: endSpan };
        }

        endSpan();
    }

    addLog(
        logAttributes: LogAttributes,
        severity: CoralogixLogSeverity.Warn | CoralogixLogSeverity.Info = CoralogixLogSeverity.Info
    ) {
        if (!parentSpan) {
            /* DO NOT USE .log - only as a fallback */
            CoralogixRum.log(
                CoralogixLogSeverity.Warn,
                "Couldn't add log, parent span has not been created",
                null,
                logAttributes
            );
            return;
        }

        if (parentSpan) {
            parentSpan
                .startCustomSpan(InstrumentTraceType.Log, {
                    ...logAttributes,
                    [LogSeverity]: severity,
                    [TraceType]: InstrumentTraceType.Log,
                })
                .endSpan();
        }
    }

    static addMetrics(metrics: MetricAttributes) {
        for (const [name, value] of Object.entries(metrics)) {
            CoralogixRum.sendCustomMeasurement(name, value);
        }
    }

    static setUserContext = setUserContext;

    static updateLabels = updateLabels;
}

export function useObservability() {
    const traceRef = useRef<{
        trace: Observability;
        closeTrace: () => void;
    } | null>(null);

    if (!traceRef.current) {
        const result = Observability.createTrace();
        traceRef.current = result;
    }

    useEffect(() => {
        return () => {
            if (traceRef.current) {
                traceRef.current.closeTrace();
                traceRef.current = null;
            }
        };
    }, []);

    function addAnalytics(
        analyticsAttributes: AnalyticsAttributes,
        options: { persist: true }
    ): { endAnalytics: () => void };
    function addAnalytics(analyticsAttributes: AnalyticsAttributes, options?: { persist?: false }): void;
    function addAnalytics(
        analyticsAttributes: AnalyticsAttributes,
        options: { persist?: boolean } = { persist: false }
    ) {
        if (!traceRef.current) {
            /* DO NOT USE .log - only as a fallback */
            CoralogixRum.log(
                CoralogixLogSeverity.Warn,
                "Couldn't add analytics, trace has not been initialised",
                null,
                analyticsAttributes
            );
            if (options.persist) {
                return { endAnalytics: () => {} };
            }
            return;
        }

        if (options.persist) {
            return traceRef.current.trace.addAnalytics(analyticsAttributes, { persist: true });
        }
        return traceRef.current.trace.addAnalytics(analyticsAttributes);
    }

    const addLog = (
        logAttributes: LogAttributes,
        severity: CoralogixLogSeverity.Warn | CoralogixLogSeverity.Info = CoralogixLogSeverity.Info
    ) => {
        if (!traceRef.current) {
            /* DO NOT USE .log - only as a fallback */
            CoralogixRum.log(
                CoralogixLogSeverity.Warn,
                "Couldn't add log, trace has not been initialised",
                null,
                logAttributes
            );
            return;
        }
        traceRef.current.trace.addLog(logAttributes, severity);
    };

    const { addMetrics } = Observability;

    const closeTrace = () => {
        if (traceRef.current) {
            traceRef.current.closeTrace();
            traceRef.current = null;
        }
    };

    return {
        addAnalytics,
        addLog,
        addMetrics,
        closeTrace,
        setUserContext,
        updateLabels,
    };
}
